import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import styled from "@emotion/styled";
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AddPaymentModal from "./components/AddPaymentModal";
import FinancialsControls from "./components/FinancialsControls";
import DomainDisabledIcon from '@mui/icons-material/DomainDisabled';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MortgagePaymentsChart from "./components/MortgagePaymentsChart";
import { selectTeamMortgages } from "../../../../slices/teamMortgagesSlice";
import PortfolioFinancialsChart from "./components/PortfolioFinancialsChart";
import { useDispatch, useSelector } from "react-redux";
import { selectViewingMortgageFinancials, setViewingMortgageFinancials } from "../../../../slices/viewingMortgageFinancialsSlice";
import { selectTeamPortfolioStatsAll, selectTeamPortfolioStatsOneYear, selectTeamPortfolioStatsTwoYear } from "../../../../slices/teamMonthlyStatsSlice";

const PortfolioFinancials = ({ teamId, userFullName }) => {
  const dispatch = useDispatch()

  const teamMortgages = useSelector(selectTeamMortgages)
  const teamPortfolioStatsAll = useSelector(selectTeamPortfolioStatsAll)
  const teamPortfolioStatsTwoYear = useSelector(selectTeamPortfolioStatsTwoYear)
  const teamPortfolioStatsOneYear = useSelector(selectTeamPortfolioStatsOneYear)
  const mortgageFinancials = useSelector(selectViewingMortgageFinancials)

  const [displaySelection, setDisplaySelection] = useState('Portfolio Overview')
  const handleDisplayChange = (event) => {
    setDisplaySelection(event.target.value)
  }

  const [timelineSelection, setTimelineSelection] = useState('All')
  const handleTimelineChange = async (event) => {
    setTimelineSelection(event.target.value)
  }
  
  const [financialsPresent, setFinancialsPresent] = useState(teamMortgages && teamMortgages[0] && teamMortgages[0].financialsPresent ? true : false)
  const [paymentsToView, setPaymentsToView] = useState(teamMortgages && teamMortgages[0] && teamMortgages[0].financialsPresent ? teamMortgages[0].payments : [])
  const [selectedMortgage, setSelectedMortgage] = useState(teamMortgages && teamMortgages[0] && teamMortgages[0].financialsPresent ? teamMortgages[0]._id : 'empty')

  const handleSetPaymentsToView = (event) => {
    setSelectedMortgage(event.target.value)
    let match = teamMortgages.find(mortgage => mortgage._id.toString() === event.target.value)
    if (match.financialsPresent) {
      setPaymentsToView(match.payments)
      dispatch( setViewingMortgageFinancials({
        mortgageId: match._id,
        monthlyPayments: match.monthlyPayments,
        principalRemaining: match.principalRemaining,
        interestRemaining: match.interestRemaining,
        remainingTerm: match.remainingTerm,
        originalLoanAmount: match.originalLoanAmount,
        originalInterestDue: match.originalInterestDue,
      }) )
      setFinancialsPresent(true)
    } else if (match) {
      setFinancialsPresent(false)
      setPaymentsToView([])
      dispatch( setViewingMortgageFinancials(null) )
    }
  }

  const [viewingPaymentWindow, setViewingPaymentWindow] = useState(false)
  const handleOpenAddPaymentWindow = () => {
    setViewingPaymentWindow(true)
  }
  const handleCloseAddPaymentWindow = () => {
    setViewingPaymentWindow(false)
  }

  useEffect(() => {
    let matched = false
    if (teamMortgages && selectedMortgage !== 'empty') {
      let match = teamMortgages.find(mortgage => mortgage._id.toString() === selectedMortgage.toString())
      if (match) {
        matched = true
        setSelectedMortgage(match._id.toString())
        setPaymentsToView(match.payments)
        dispatch( setViewingMortgageFinancials({
          mortgageId: match._id,
          monthlyPayments: match.monthlyPayments,
          principalRemaining: match.principalRemaining,
          interestRemaining: match.interestRemaining,
          remainingTerm: match.remainingTerm,
          originalLoanAmount: match.originalLoanAmount,
          originalInterestDue: match.originalInterestDue,
        }) )
      }
    }
    if (!matched && teamMortgages && teamMortgages[0]) {
      setSelectedMortgage(teamMortgages[0]._id.toString())
      setPaymentsToView(teamMortgages[0].payments)
      dispatch( setViewingMortgageFinancials({
        mortgageId: teamMortgages[0]._id,
        monthlyPayments: teamMortgages[0].monthlyPayments,
        principalRemaining: teamMortgages[0].principalRemaining,
        interestRemaining: teamMortgages[0].interestRemaining,
        remainingTerm: teamMortgages[0].remainingTerm,
        originalLoanAmount: teamMortgages[0].originalLoanAmount,
        originalInterestDue: teamMortgages[0].originalInterestDue,
      }) )
    }
  }, [teamMortgages])

  const renderChartContent = () => {
    if (displaySelection === 'Mortgage Payments') {
      if (paymentsToView.length > 0 && financialsPresent) {
        return <MortgagePaymentsChart paymentsToView={paymentsToView} />
      }

      if (!financialsPresent) {
        return (
          <EmptyState>
            <MoneyOffIcon />
            <EmptyTitle>Missing original loan terms</EmptyTitle>
            <EmptySummary>Provide original terms before reviewing this mortgage's payment history.</EmptySummary>
            <EmptyAction type="button">
              <AddCircleOutlineIcon />
              Provide Original Terms
            </EmptyAction>
          </EmptyState>
        )
      }

      return (
        <EmptyState>
          <DomainDisabledIcon />
          <EmptyTitle>
            {teamMortgages.length === 0 ? `No mortgages have been uploaded` : `No mortgages match the selected criteria`}
          </EmptyTitle>
          <EmptySummary>Select a mortgage with payment history to populate the chart.</EmptySummary>
        </EmptyState>
      )
    }

    if (displaySelection === 'Portfolio Overview' && teamPortfolioStatsAll && teamPortfolioStatsAll.length > 0) {
      if (timelineSelection === '2 Year') {
        return <PortfolioFinancialsChart teamPortfolioStats={teamPortfolioStatsTwoYear} teamMortgagesNum={teamMortgages.length} />
      }

      if (timelineSelection === '1 Year') {
        return <PortfolioFinancialsChart teamPortfolioStats={teamPortfolioStatsOneYear} teamMortgagesNum={teamMortgages.length} />
      }

      return <PortfolioFinancialsChart teamPortfolioStats={teamPortfolioStatsAll} teamMortgagesNum={teamMortgages.length} />
    }

    return (
      <EmptyState>
        <DomainDisabledIcon />
        <EmptyTitle>No portfolio financials available</EmptyTitle>
        <EmptySummary>Financial trends will appear after mortgages with original terms and payments are available.</EmptySummary>
      </EmptyState>
    )
  }

  return (
    <>
      <PortfolioPage>
        <PortfolioWorkspace>
          <WorkspaceHeader>
            <div>
              <WorkspaceEyebrow>Financial Analytics</WorkspaceEyebrow>
              <WorkspaceTitle>Portfolio Financials</WorkspaceTitle>
            </div>
            <WorkspaceSummary>
              Review portfolio-level financial trends and individual mortgage payment history across selected timelines.
            </WorkspaceSummary>
          </WorkspaceHeader>

          <WorkspaceBody>
            <FinancialsControls
              teamMortgages={teamMortgages}
              displaySelection={displaySelection}
              handleDisplayChange={handleDisplayChange}
              handleSetPaymentsToView={handleSetPaymentsToView}
              selectedMortgage={selectedMortgage}
              mortgageFinancials={mortgageFinancials}
              handleOpenAddPaymentWindow={handleOpenAddPaymentWindow}
              timelineSelection={timelineSelection}
              handleTimelineChange={handleTimelineChange}
              financialsPresent={financialsPresent}
              teamPortfolioStatsOneYear={teamPortfolioStatsOneYear}
            />

            <ChartSurface>
              <ChartHeader>
                <div>
                  <ChartEyebrow>{displaySelection === 'Mortgage Payments' ? 'Mortgage Payment History' : 'Portfolio Trend'}</ChartEyebrow>
                  <ChartTitle>{displaySelection}</ChartTitle>
                </div>
                <ChartContext>
                  {displaySelection === 'Mortgage Payments'
                    ? 'Principal, interest, and remaining balance for the selected mortgage.'
                    : `${timelineSelection} financial performance across the active portfolio.`}
                </ChartContext>
              </ChartHeader>
              <ChartFrame>
                {renderChartContent()}
              </ChartFrame>
            </ChartSurface>
          </WorkspaceBody>
        </PortfolioWorkspace>
      </PortfolioPage>

      <Dialog open={viewingPaymentWindow} onClose={handleCloseAddPaymentWindow}
        PaperProps={{ sx: {width: "50rem", minWidth: "50rem", maxWidth: "50rem", height: "35rem", minHeight: "35rem", maxHeight: "35rem"}}}
        tabIndex={-1}
      >
        <AddPaymentModal handleCloseModal={handleCloseAddPaymentWindow} userFullName={userFullName} teamId={teamId} mortgageFinancials={mortgageFinancials} />
      </Dialog>
    </>
  )
}

const PortfolioPage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 0.75rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const PortfolioWorkspace = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const WorkspaceHeader = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.3rem;
  padding: 0.85rem 1.15rem 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const WorkspaceEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.15rem;
  color: ${theme.palette.primary.main};
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const WorkspaceTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.42rem;
  font-weight: 800;
  line-height: 1.15;
`)

const WorkspaceSummary = styled.p(({ theme }) => `
  max-width: 66rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.86rem;
  font-weight: 550;
  line-height: 1.32;
`)

const WorkspaceBody = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 0.75rem;
  padding: 0.75rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const ChartSurface = styled.section(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const ChartHeader = styled.header(({ theme }) => `
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: end;
  padding: 0.65rem 0.8rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const ChartEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.12rem;
  color: ${theme.palette.primary.main};
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const ChartTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 0.98rem;
  font-weight: 850;
  line-height: 1.2;
`)

const ChartContext = styled.p(({ theme }) => `
  max-width: 34rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.74rem;
  font-weight: 650;
  line-height: 1.25;
  text-align: right;
`)

const ChartFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: 0.65rem 0.65rem 0.45rem;
  overflow: hidden;
`

const EmptyState = styled.div(({ theme }) => `
  width: min(100%, 28rem);
  min-height: 14rem;
  margin: auto;
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 0.55rem;
  padding: 1.25rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.cardBackground};
  color: ${theme.palette.common.grayText};
  text-align: center;

  svg {
    width: 2.6rem;
    height: 2.6rem;
    color: ${theme.palette.common.grayBorderDark};
  }
`)

const EmptyTitle = styled.h3(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 850;
  line-height: 1.2;
`)

const EmptySummary = styled.p(({ theme }) => `
  max-width: 23rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.35;
`)

const EmptyAction = styled.button(({ theme }) => `
  height: 2.3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.2rem;
  padding: 0 0.8rem;
  border: 0.1rem solid ${theme.accent.caution.primary};
  border-radius: 0.55rem;
  background: ${theme.tooltip.caution.background};
  color: ${theme.accent.caution.primary};
  font-size: 0.78rem;
  font-weight: 850;

  svg {
    width: 1rem;
    height: 1rem;
    color: currentColor;
  }
`)

export default PortfolioFinancials
